<template>
  <div class="sovi-esign-security-page sovi-home">
    <router-link to="/" class="sovi-esign-security-page__logo">
      <sovi-logo :size="48" />
    </router-link>

    <v-menu offset-y>
      <template #activator="{ props }">
        <v-btn
          class="language-menu"
          size="default"
          color="#eca1c2"
          v-bind="props"
        >
          <template #prepend>
            <v-icon icon="mdi-web" />
          </template>

          {{ $i18n.locale.toUpperCase() }}
        </v-btn>
      </template>

      <v-list>
        <v-list-item
          v-for="option in $i18n.availableLocales"
          :key="option"
          class="align-items-center"
          @click="onLocaleChange(option)"
        >
          <v-row align="center" no-gutters>
            {{ option.toUpperCase() }}
          </v-row>
        </v-list-item>
      </v-list>
    </v-menu>

    <sovi-content-nav />

    <!-- Hero -->
    <sovi-home-card
      background="/images/content-17.webp"
      src="/images/content-11.webp"
      :title="t('heroTitle')"
      fixed
      style="padding-top: 100px"
    >
      <strong>{{ t('heroSubtitle') }}</strong>

      <p>{{ t('heroText') }}</p>
    </sovi-home-card>

    <!-- Legal validity -->
    <sovi-home-card
      src="/images/content-19.webp"
      :title="t('legalTitle')"
      flipped
    >
      <p>{{ t('legalText1') }}</p>

      <p>{{ t('legalText2') }}</p>

      <p>{{ t('legalText3') }}</p>

      <p>{{ t('legalNote') }}</p>
    </sovi-home-card>

    <!-- Security -->
    <sovi-home-card src="/images/content-20.webp" :title="t('securityTitle')">
      <p>{{ t('securityText') }}</p>

      <ul class="ms-6 list-disc">
        <li>{{ t('securityItem1') }}</li>

        <li>{{ t('securityItem2') }}</li>

        <li>{{ t('securityItem3') }}</li>

        <li>{{ t('securityItem4') }}</li>

        <li>{{ t('securityItem5') }}</li>

        <li>{{ t('securityItem6') }}</li>
      </ul>

      <p>{{ t('securityConclusion') }}</p>
    </sovi-home-card>

    <!-- Comparison table -->
    <sovi-home-card
      src="/images/content-21.webp"
      :title="t('comparisonTitle')"
      flipped
    >
      <v-table>
        <thead>
          <tr>
            <th>{{ t('comparisonFeature') }}</th>

            <th>{{ t('comparisonPaper') }}</th>

            <th>{{ t('comparisonDigital') }}</th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td>{{ t('comparisonSpeed') }}</td>

            <td>{{ t('comparisonSpeedPaper') }}</td>

            <td>{{ t('comparisonSpeedDigital') }}</td>
          </tr>

          <tr>
            <td>{{ t('comparisonRemote') }}</td>

            <td>{{ t('comparisonRemotePaper') }}</td>

            <td>{{ t('comparisonRemoteDigital') }}</td>
          </tr>

          <tr>
            <td>{{ t('comparisonPrinting') }}</td>

            <td>{{ t('comparisonPrintingPaper') }}</td>

            <td>{{ t('comparisonPrintingDigital') }}</td>
          </tr>

          <tr>
            <td>{{ t('comparisonScanning') }}</td>

            <td>{{ t('comparisonScanningPaper') }}</td>

            <td>{{ t('comparisonScanningDigital') }}</td>
          </tr>

          <tr>
            <td>{{ t('comparisonMailing') }}</td>

            <td>{{ t('comparisonMailingPaper') }}</td>

            <td>{{ t('comparisonMailingDigital') }}</td>
          </tr>

          <tr>
            <td>{{ t('comparisonTracking') }}</td>

            <td>{{ t('comparisonTrackingPaper') }}</td>

            <td>{{ t('comparisonTrackingDigital') }}</td>
          </tr>

          <tr>
            <td>{{ t('comparisonArchiving') }}</td>

            <td>{{ t('comparisonArchivingPaper') }}</td>

            <td>{{ t('comparisonArchivingDigital') }}</td>
          </tr>

          <tr>
            <td>{{ t('comparisonEnvironment') }}</td>

            <td>{{ t('comparisonEnvironmentPaper') }}</td>

            <td>{{ t('comparisonEnvironmentDigital') }}</td>
          </tr>

          <tr>
            <td>{{ t('comparisonWorkTime') }}</td>

            <td>{{ t('comparisonWorkTimePaper') }}</td>

            <td>{{ t('comparisonWorkTimeDigital') }}</td>
          </tr>
        </tbody>
      </v-table>

      <p>{{ t('comparisonConclusion') }}</p>
    </sovi-home-card>

    <!-- FAQ link -->
    <sovi-home-card src="/images/content-22.webp" :title="t('faqTitle')">
      <p>{{ t('faqText') }}</p>

      <template #button>
        <router-link to="/ukk" class="text-none justify-self-start">
          <v-btn color="primary" class="text-none">
            {{ t('faqLink') }}
            <template #append>
              <v-icon class="mt-1">mdi-arrow-right</v-icon>
            </template>
          </v-btn>
        </router-link>
      </template>
    </sovi-home-card>

    <!-- CTA section -->
    <sovi-home-card
      src="/images/content-12.webp"
      :title="t('ctaTitle')"
      flipped
    >
      <p>{{ t('ctaIntro') }}</p>

      <ul class="ms-6 list-disc">
        <li>{{ t('ctaFeature1') }}</li>

        <li>{{ t('ctaFeature2') }}</li>

        <li>{{ t('ctaFeature3') }}</li>

        <li>{{ t('ctaFeature4') }}</li>

        <li>{{ t('ctaFeature5') }}</li>

        <li>{{ t('ctaFeature6') }}</li>
      </ul>

      <p>
        <strong>{{ t('ctaAction') }}</strong>
      </p>
    </sovi-home-card>

    <!-- Navigation links -->
    <div
      style="
        padding: clamp(32px, 5vw, 64px) clamp(16px, 5vw, 32px);
        background-color: #fff;
      "
    >
      <div style="display: grid; gap: 8px">
        <router-link to="/sahkoinen-allekirjoitus">
          {{ t('navMain') }} →
        </router-link>

        <router-link to="/sahkoinen-allekirjoitus-yrityksille">
          {{ t('navBusiness') }} →
        </router-link>

        <router-link to="/ukk">{{ t('navFaq') }} →</router-link>
      </div>
    </div>

    <div class="sovi-esign-security-page__login">
      <sovi-home-login />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTheme } from 'vuetify';
import { definePage } from 'vue-router/auto';
import { useRouteMeta } from '@/composables/useRouteMeta';
import { useAuthorizationStore } from '@/stores/authorization';
import { loadRecaptcha } from '@/plugins/recaptcha';
import SoviHomeLogin from '@/components/home/SoviHomeLogin.vue';

definePage({
  meta: {
    hero: false,
    restricted: false,
    maxContainer: true,
  },
});

useRouteMeta({
  title:
    'Sähköisen allekirjoituksen turvallisuus ja juridinen pätevyys | Sovi.fi',
  description:
    'Sähköinen allekirjoitus on juridisesti pätevä EU:n eIDAS-asetuksen mukaisesti. Lue lisää tietoturvasta, jäljitettävyydestä ja paperisen sekä sähköisen allekirjoituksen eroista.',
});

const { locale } = useI18n({ useScope: 'global' });
const { t } = useI18n();
const theme = useTheme();
const { isAuthorized } = useAuthorizationStore();

const onLocaleChange = (newLocale: string) => {
  locale.value = newLocale;
  localStorage.setItem('sovi-locale', newLocale);
};

onMounted(() => {
  document.getElementById('sovi-main')?.scrollTo(0, 0);
  window.scrollTo(0, 0);
  loadRecaptcha();

  const storedLocale = localStorage.getItem('sovi-locale');

  if (storedLocale && storedLocale !== locale.value) {
    locale.value = storedLocale;
  }

  theme.global.name.value = 'light';

  isAuthorized();
});
</script>

<style lang="scss">
.grecaptcha-badge {
  visibility: hidden;
}

.sovi-esign-security-page {
  position: relative;
  background-color: #fff;
  font-size: clamp(18px, 2vw, 22px);

  &__logo {
    position: absolute;
    top: 16px;
    left: 16px;
    z-index: 2;
  }

  .language-menu {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 2;
    box-shadow:
      rgba(0, 0, 0, 0.15) 0px 15px 25px,
      rgba(0, 0, 0, 0.05) 0px 5px 10px;
  }

  .v-table {
    max-width: 100%;
    overflow-x: auto;
    display: block;
    font-size: clamp(12px, 1.5vw, 16px);
  }

  .home-title {
    line-height: 1.3;

    @media (min-width: 768px) {
      line-height: 1.1;
    }
    --title-color: rgb(var(--v-theme-primary));

    &.alt {
      --title-color: #eca1c2;
    }

    &::first-letter {
      padding-bottom: 2px;
      box-shadow: inset 0 calc(-1 * clamp(4px, 0.5vw, 6px)) 0 0
        var(--title-color);
    }
  }

  &__login {
    overflow: hidden;

    .sovi-home__login {
      &::before,
      &::after {
        display: none;
      }
    }
    background-color: #fff;
    padding: clamp(32px, 5vw, 64px) clamp(16px, 5vw, 32px);

    .sovi-home__login-form {
      display: grid;
      gap: clamp(16px, 3vw, 32px);
      position: relative;
      z-index: 1;

      h1 {
        font-size: clamp(24px, 3vw, 32px);
      }
    }
  }
}
</style>

<i18n lang="json">
{
  "fi": {
    "heroTitle": "Sähköisen allekirjoituksen turvallisuus ja juridinen pätevyys",
    "heroSubtitle": "Tietoturva, eIDAS-asetus ja vertailu perinteiseen allekirjoitukseen",
    "heroText": "Sähköinen allekirjoitus on juridisesti pätevä ja turvallinen tapa allekirjoittaa sopimuksia. Tutustu tietoturvakäytäntöihin, oikeudelliseen taustaan ja paperisen sekä sähköisen allekirjoituksen eroihin.",
    "legalTitle": "Sähköinen allekirjoitus on juridisesti pätevä",
    "legalText1": "Yksi yleisimmistä kysymyksistä sähköiseen allekirjoitukseen liittyen on sen oikeudellinen pätevyys. Oikein toteutettu sähköinen allekirjoitus on juridisesti sitova ja sitä voidaan käyttää useimmissa sopimuksissa samalla tavoin kuin perinteistä käsin tehtyä allekirjoitusta.",
    "legalText2": "EU:n eIDAS-asetus määrittelee sähköisten allekirjoitusten oikeudellisen aseman koko Euroopan unionin alueella. Tämä luo yhteiset pelisäännöt ja lisää luottamusta digitaaliseen sopimisprosessiin myös kansainvälisessä liiketoiminnassa.",
    "legalText3": "Käytännössä tämä tarkoittaa, että yritykset voivat siirtyä sähköiseen allekirjoitukseen turvallisesti ilman, että sopimusten oikeudellinen arvo heikkenee.",
    "legalNote": "Tietyissä erityistilanteissa lainsäädäntö voi kuitenkin edellyttää määrämuotoista menettelyä. Yrityksen kannattaa aina varmistaa omien asiakirjojensa mahdolliset erityisvaatimukset.",
    "securityTitle": "Tietoturva on sähköisen allekirjoituksen perusta",
    "securityText": "Sopimukset sisältävät usein arkaluonteisia tietoja, kuten henkilötietoja, liikesalaisuuksia, palkkatietoja tai kaupallisia ehtoja. Hyvä sähköinen allekirjoituspalvelu suojaa asiakirjat koko niiden elinkaaren ajan:",
    "securityItem1": "asiakirjojen turvallinen siirto",
    "securityItem2": "turvallinen tallennus",
    "securityItem3": "käyttöoikeuksien hallinta",
    "securityItem4": "tapahtumaloki",
    "securityItem5": "allekirjoitusprosessin seuranta",
    "securityItem6": "asiakirjan eheyden varmistaminen",
    "securityConclusion": "Kun kaikki vaiheet dokumentoidaan automaattisesti, yrityksellä on myöhemmin selkeä näyttö siitä, miten sopimus on syntynyt.",
    "comparisonTitle": "Paperinen vai sähköinen allekirjoitus?",
    "comparisonFeature": "Ominaisuus",
    "comparisonPaper": "Paperinen allekirjoitus",
    "comparisonDigital": "Sähköinen allekirjoitus",
    "comparisonSpeed": "Allekirjoituksen nopeus",
    "comparisonSpeedPaper": "Päivistä viikkoihin",
    "comparisonSpeedDigital": "Usein muutamassa minuutissa",
    "comparisonRemote": "Allekirjoitus etänä",
    "comparisonRemotePaper": "Vaikeaa",
    "comparisonRemoteDigital": "Kyllä",
    "comparisonPrinting": "Tulostaminen",
    "comparisonPrintingPaper": "Tarvitaan usein",
    "comparisonPrintingDigital": "Ei tarvita",
    "comparisonScanning": "Skannaaminen",
    "comparisonScanningPaper": "Tarvitaan usein",
    "comparisonScanningDigital": "Ei tarvita",
    "comparisonMailing": "Postitus",
    "comparisonMailingPaper": "Usein tarpeen",
    "comparisonMailingDigital": "Ei tarvita",
    "comparisonTracking": "Asiakirjojen seuranta",
    "comparisonTrackingPaper": "Manuaalista",
    "comparisonTrackingDigital": "Reaaliaikainen seuranta",
    "comparisonArchiving": "Arkistointi",
    "comparisonArchivingPaper": "Paperiarkisto tai verkkolevy",
    "comparisonArchivingDigital": "Keskitetty digitaalinen arkisto",
    "comparisonEnvironment": "Ympäristövaikutus",
    "comparisonEnvironmentPaper": "Suurempi",
    "comparisonEnvironmentDigital": "Pienempi",
    "comparisonWorkTime": "Työaika",
    "comparisonWorkTimePaper": "Enemmän manuaalisia vaiheita",
    "comparisonWorkTimeDigital": "Selvästi tehokkaampi",
    "comparisonConclusion": "Sähköiseen allekirjoitukseen siirtyminen ei ole pelkästään tekninen uudistus. Se on tapa yksinkertaistaa koko sopimusprosessia, vähentää viivästyksiä ja vapauttaa henkilöstön aikaa liiketoiminnan kannalta tärkeämpiin tehtäviin.",
    "faqTitle": "Usein kysytyt kysymykset",
    "faqText": "Löydä vastaukset yleisimpiin kysymyksiin sähköisestä allekirjoituksesta, sen juridisesta pätevyydestä, tietoturvasta ja käyttötavoista.",
    "faqLink": "Katso usein kysytyt kysymykset",
    "ctaTitle": "Aloita sähköinen allekirjoitus Sovi.fi:ssä",
    "ctaIntro": "Olipa kyse yksittäisestä sopimuksesta tai tuhansista asiakirjoista vuodessa, Sovi.fi tarjoaa helpon ja turvallisen ratkaisun sähköiseen allekirjoittamiseen. Sovi.fi:n avulla voit:",
    "ctaFeature1": "lähettää asiakirjoja allekirjoitettavaksi muutamassa minuutissa",
    "ctaFeature2": "seurata allekirjoitusprosessin etenemistä reaaliaikaisesti",
    "ctaFeature3": "käyttää valmiita asiakirjamalleja",
    "ctaFeature4": "hallita dokumentteja kansioissa",
    "ctaFeature5": "vähentää paperityötä ja manuaalisia työvaiheita",
    "ctaFeature6": "nopeuttaa sopimusprosesseja koko organisaatiossa",
    "ctaAction": "Luo maksuton tili ja lähetä ensimmäinen asiakirjasi allekirjoitettavaksi muutamassa minuutissa.",
    "navMain": "Sähköinen allekirjoitus",
    "navBusiness": "Sähköinen allekirjoitus yrityksille",
    "navFaq": "Usein kysytyt kysymykset"
  },
  "en": {
    "heroTitle": "Electronic Signature Security and Legal Validity",
    "heroSubtitle": "Data security, eIDAS regulation, and comparison with traditional signatures",
    "heroText": "Electronic signatures are legally valid and a secure way to sign contracts. Learn about security practices, the legal background, and the differences between paper and electronic signatures.",
    "legalTitle": "Electronic signatures are legally valid",
    "legalText1": "One of the most common questions about electronic signatures concerns their legal validity. A properly implemented electronic signature is legally binding and can be used in most contracts in the same way as a traditional handwritten signature.",
    "legalText2": "The EU's eIDAS regulation defines the legal status of electronic signatures across the entire European Union. This creates common rules and increases trust in the digital contracting process, including in international business.",
    "legalText3": "In practice, this means businesses can transition to electronic signatures securely without compromising the legal value of their contracts.",
    "legalNote": "In certain special cases, legislation may require specific formal procedures. Businesses should always verify any special requirements for their particular documents.",
    "securityTitle": "Security is the foundation of electronic signatures",
    "securityText": "Contracts often contain sensitive information such as personal data, trade secrets, salary details, or commercial terms. A good electronic signature service protects documents throughout their entire lifecycle:",
    "securityItem1": "secure document transfer",
    "securityItem2": "secure storage",
    "securityItem3": "access control management",
    "securityItem4": "event logging",
    "securityItem5": "signing process tracking",
    "securityItem6": "document integrity verification",
    "securityConclusion": "When all steps are documented automatically, the business has clear evidence later of how the agreement was formed.",
    "comparisonTitle": "Paper vs. electronic signature",
    "comparisonFeature": "Feature",
    "comparisonPaper": "Paper signature",
    "comparisonDigital": "Electronic signature",
    "comparisonSpeed": "Signing speed",
    "comparisonSpeedPaper": "Days to weeks",
    "comparisonSpeedDigital": "Often within minutes",
    "comparisonRemote": "Remote signing",
    "comparisonRemotePaper": "Difficult",
    "comparisonRemoteDigital": "Yes",
    "comparisonPrinting": "Printing",
    "comparisonPrintingPaper": "Often required",
    "comparisonPrintingDigital": "Not needed",
    "comparisonScanning": "Scanning",
    "comparisonScanningPaper": "Often required",
    "comparisonScanningDigital": "Not needed",
    "comparisonMailing": "Postal delivery",
    "comparisonMailingPaper": "Often required",
    "comparisonMailingDigital": "Not needed",
    "comparisonTracking": "Document tracking",
    "comparisonTrackingPaper": "Manual",
    "comparisonTrackingDigital": "Real-time tracking",
    "comparisonArchiving": "Archiving",
    "comparisonArchivingPaper": "Paper archive or network drive",
    "comparisonArchivingDigital": "Centralized digital archive",
    "comparisonEnvironment": "Environmental impact",
    "comparisonEnvironmentPaper": "Higher",
    "comparisonEnvironmentDigital": "Lower",
    "comparisonWorkTime": "Work time",
    "comparisonWorkTimePaper": "More manual steps",
    "comparisonWorkTimeDigital": "Significantly more efficient",
    "comparisonConclusion": "Switching to electronic signatures is not just a technical upgrade. It's a way to simplify the entire contracting process, reduce delays, and free up your team's time for more important business tasks.",
    "faqTitle": "Frequently asked questions",
    "faqText": "Find answers to the most common questions about electronic signatures, their legal validity, security, and usage.",
    "faqLink": "View frequently asked questions",
    "ctaTitle": "Start signing electronically with Sovi.fi",
    "ctaIntro": "Whether it's a single contract or thousands of documents per year, Sovi.fi offers an easy and secure solution for electronic signing. With Sovi.fi you can:",
    "ctaFeature1": "send documents for signing in minutes",
    "ctaFeature2": "track signing progress in real time",
    "ctaFeature3": "use ready-made document templates",
    "ctaFeature4": "manage documents in folders",
    "ctaFeature5": "reduce paperwork and manual steps",
    "ctaFeature6": "speed up contracting processes across the organization",
    "ctaAction": "Create a free account and send your first document for signing in just a few minutes.",
    "navMain": "Electronic signatures",
    "navBusiness": "Electronic signatures for businesses",
    "navFaq": "Frequently asked questions"
  }
}
</i18n>
